import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChartBarIcon, 
  AcademicCapIcon, 
  GlobeAltIcon, 
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { SCHOOL_INFO } from '../config/schoolPrompts';
import { useApplication } from '../context/ApplicationContext';

const HomePage = () => {
  const navigate = useNavigate();
  const { setSchool } = useApplication();

  const handleSchoolSelect = (school) => {
    setSchool(school);
    navigate(`/simulator/${school.value}`);
  };

  const availableSchools = [
    { value: 'INSEAD', label: 'INSEAD', description: 'The Business School for the World' },
    { value: 'YALE_SOM', label: 'Yale SOM', description: 'Educating leaders for business & society' }
  ];

  const features = [
    {
      icon: ChartBarIcon,
      title: 'AI-Powered Evaluation',
      description: 'Get personalized insights based on your profile and school-specific criteria.'
    },
    {
      icon: AcademicCapIcon,
      title: 'School-Specific Assessment',
      description: 'Each evaluation is tailored to the unique culture and values of top business schools.'
    },
    {
      icon: GlobeAltIcon,
      title: 'Holistic Review Process',
      description: 'Experience a comprehensive evaluation that considers all aspects of your application.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Direct School Selection */}
      <section className="relative bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Discover Your MBA
              <span className="block text-yellow-300">Admission Chances</span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 max-w-2xl mx-auto text-blue-100">
              Get AI-powered insights into your MBA application chances with personalized evaluations for top business schools.
            </p>
            
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-6 text-yellow-300">Choose Your Target School</h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {availableSchools.map((school) => (
                  <button
                    key={school.value}
                    onClick={() => handleSchoolSelect(school)}
                    className="group bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 hover:bg-opacity-20 transition-all duration-300 border border-white border-opacity-20 hover:border-opacity-40"
                  >
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {school.label}
                      </h3>
                      <p className="text-blue-100 mb-4">
                        {school.description}
                      </p>
                      <div className="flex items-center justify-center text-yellow-300 group-hover:text-yellow-200">
                        <span className="mr-2">Start Evaluation</span>
                        <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Our MBA Simulator?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our advanced AI system provides comprehensive evaluation based on real admissions criteria and school-specific requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                  <feature.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* School Information Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Supported Schools
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get detailed evaluations tailored to each school's unique culture, values, and admission criteria.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {Object.entries(SCHOOL_INFO).map(([key, school]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {school.fullName}
                </h3>
                <p className="text-primary-600 font-semibold mb-4">
                  {school.description}
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-600">Class Size</p>
                    <p className="font-semibold text-gray-900">{school.classSize}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Program Length</p>
                    <p className="font-semibold text-gray-900">{school.programLength}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Average GMAT</p>
                    <p className="font-semibold text-gray-900">{school.averageGMAT}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">International %</p>
                    <p className="font-semibold text-gray-900">{school.internationalPercentage}%</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {school.locations.map((location, index) => (
                    <span key={index} className="inline-block bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm">
                      {location}
                    </span>
                  ))}
                </div>

                <button
                  onClick={() => handleSchoolSelect({ value: key, label: school.name, description: school.description })}
                  className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold"
                >
                  Evaluate for {school.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>


    </div>
  );
};

export default HomePage; 